<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <h2 class="font-weight-bold mb-6">{{ $t('carbonCredit.activityLog.title') }}</h2>

      <v-card elevation="0" class="px-5 mt-2">
        <v-card-text>
          <v-data-table
            :headers="tableHeaders"
            :items="activityLogs"
            :items-per-page="options.limit"
            item-key="id"
            hide-default-footer
            :loading="loading"
            :loading-text="$t('loadingData')"
            class="cc-activity-log-table"
          >
            <template v-slot:top>
              <div class="d-flex align-center flex-wrap mt-5 mb-2">
                <div class="ml-3" style="width: 280px;">
                  <v-text-field
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    :placeholder="$t('carbonCredit.search')"
                    v-model="search"
                    clearable
                    @input="onSearchInput"
                  />
                </div>
                <div class="ml-3" style="width: 220px;">
                  <v-select
                    v-model="segmentFilter"
                    :items="segmentOptions"
                    item-text="text"
                    item-value="value"
                    outlined
                    dense
                    clearable
                    :label="$t('carbonCredit.activityLog.segment')"
                    :placeholder="$t('carbonCredit.select')"
                    @change="fetchActivityLogs"
                  />
                </div>
                <v-spacer />
                <div class="py-1 title">
                  {{ fromRow }} - {{ toRow }} {{ $t('of') }} {{ totalCount }}
                </div>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="options.page <= 1 || loading"
                  @click="pageChange(false)"
                >
                  <v-icon dark>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="options.page >= totalPages || loading"
                  @click="pageChange(true)"
                >
                  <v-icon dark>mdi-chevron-right</v-icon>
                </v-btn>
                <v-menu
                  v-model="settingsMenu"
                  :close-on-content-click="false"
                  :nudge-width="200"
                  left
                  :min-width="417"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                      <v-icon dark>mdi-cog-outline</v-icon>
                    </v-btn>
                  </template>
                  <v-card width="417">
                    <v-list>
                      <v-list-item>
                        <v-list-item-title>{{ $t('customization') }}</v-list-item-title>
                      </v-list-item>
                      <v-divider />
                      <div class="pl-3 pt-2 font-weight-normal">{{ $t('recordsPerPage') }}</div>
                      <v-list-item>
                        <v-slider
                          v-model="itemsPerPageSlider"
                          :tick-labels="itemsPerPageOptions"
                          :max="3"
                          step="1"
                          ticks="always"
                          tick-size="4"
                        />
                      </v-list-item>
                      <v-divider class="mt-n3" />
                    </v-list>
                    <v-card-text>
                      <v-container fluid>
                        <v-row>
                          <v-col
                            class="mt-n9"
                            cols="6"
                            v-for="header in headers"
                            :key="header.value"
                          >
                            <v-checkbox
                              v-if="header.text"
                              v-model="selectedHeaders"
                              :label="header.text"
                              color="primary"
                              :value="header.text"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-layout justify-center>
                      <v-card-actions class="mb-5">
                        <v-spacer />
                        <v-btn outlined color="primary" @click="resetDefault" width="190">
                          {{ $t('restoreDefault') }}
                        </v-btn>
                        <v-btn color="primary" @click="applyTableSettings" width="190">
                          {{ $t('apply') }}
                        </v-btn>
                      </v-card-actions>
                    </v-layout>
                  </v-card>
                </v-menu>
              </div>
            </template>

            <template v-slot:item.userName="{ item }">
              <span class="text-truncate d-inline-block" style="max-width: 160px;">
                {{ item.userName || '-' }}
              </span>
            </template>

            <template v-slot:item.userRole="{ item }">
              {{ formatUserRole(item.userRole) }}
            </template>

            <template v-slot:item.ipAddress="{ item }">
              {{ item.ipAddress || '-' }}
            </template>

            <template v-slot:item.createdAt="{ item }">
              {{ formatDateTime(item.createdAt) }}
            </template>

            <template v-slot:item.segment="{ item }">
              {{ segmentLabel(item.module) }}
            </template>

            <template v-slot:item.description="{ item }">
              <span class="description-cell">{{ item.description || '-' }}</span>
            </template>

            <template v-slot:footer />
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import loadingMixin from '@/mixins/LoadingMixin';
import CarbonCreditService from '@/_services/CarbonCreditService';

const SEGMENT_FILTER_OPTIONS = [
  'projects',
  'project_farm',
  'tree_mapping',
  'crop_growing',
  'nfts',
  'dimitra_office',
];

export default {
  name: 'CarbonCreditActivityLog',
  mixins: [loadingMixin],
  data() {
    return {
      breadcrumbs: [
        { text: this.$t('carbonCredit.dashboard'), disabled: false, href: '/dashboard' },
        { text: this.$t('carbonCredit.title'), disabled: false, href: '/carbon-credit/projects' },
        { text: this.$t('carbonCredit.activityLog.title'), disabled: true },
      ],
      search: '',
      segmentFilter: null,
      searchDebounce: null,
      activityLogs: [],
      totalCount: 0,
      fromRow: 0,
      toRow: 0,
      options: { page: 1, limit: 10 },
      settingsMenu: false,
      itemsPerPageSlider: 1,
      itemsPerPageOptions: [5, 10, 25, 50],
      headers: [
        { text: this.$t('carbonCredit.activityLog.user'), value: 'userName', sortable: false },
        { text: this.$t('carbonCredit.activityLog.userRole'), value: 'userRole', sortable: false },
        { text: this.$t('carbonCredit.activityLog.ipAddress'), value: 'ipAddress', sortable: false },
        { text: this.$t('carbonCredit.activityLog.dateAndTime'), value: 'createdAt', sortable: false },
        { text: this.$t('carbonCredit.activityLog.segment'), value: 'segment', sortable: false },
        { text: this.$t('carbonCredit.activityLog.activityDescription'), value: 'description', sortable: false },
      ],
      selectedHeaders: [],
      tableHeaders: [],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.options.limit));
    },
    segmentOptions() {
      return SEGMENT_FILTER_OPTIONS.map((value) => ({
        value,
        text: this.segmentLabel(value),
      }));
    },
  },
  mounted() {
    this.selectedHeaders = this.headers.map((h) => h.text);
    this.tableHeaders = [...this.headers];
    this.fetchActivityLogs();
  },
  methods: {
    segmentLabel(module) {
      if (!module) return '-';
      const key = `carbonCredit.activityLog.segments.${module}`;
      const translated = this.$t(key);
      return translated !== key ? translated : module;
    },
    formatUserRole(role) {
      if (!role) return '-';
      return role
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-';
      const date = new Date(dateTimeString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${day}-${month}-${year} | ${hours.toString().padStart(2, '0')}:${minutes} ${period}`;
    },
    updatePaginationDisplay() {
      this.fromRow = this.totalCount > 0 ? (this.options.page - 1) * this.options.limit + 1 : 0;
      this.toRow =
        this.options.page * this.options.limit > this.totalCount
          ? this.totalCount
          : this.options.page * this.options.limit;
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.options.page = 1;
        this.fetchActivityLogs();
      }, 400);
    },
    pageChange(next) {
      if (next) this.options.page += 1;
      else this.options.page = Math.max(1, this.options.page - 1);
      this.fetchActivityLogs();
    },
    resetDefault() {
      this.selectedHeaders = this.headers.map((h) => h.text);
      this.itemsPerPageSlider = 1;
      this.options.limit = 10;
      this.options.page = 1;
      this.applyTableSettings();
    },
    applyTableSettings() {
      this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.tableHeaders = this.headers.filter(
        (h) => h.text && this.selectedHeaders.includes(h.text)
      );
      this.settingsMenu = false;
      this.options.page = 1;
      this.fetchActivityLogs();
    },
    async fetchActivityLogs() {
      this.startLoading();
      try {
        const res = await CarbonCreditService.getActivityLogs({
          page: this.options.page,
          limit: this.options.limit,
          searchPhrase: this.search || undefined,
          module: this.segmentFilter || undefined,
          orderField: 'createdAt',
          order: 'DESC',
        });

        this.activityLogs = res?.data?.rows || [];
        this.totalCount = res?.data?.totalCount || 0;
        this.updatePaginationDisplay();
      } catch (err) {
        console.error('Failed to load carbon credit activity logs', err);
        this.activityLogs = [];
        this.totalCount = 0;
        this.updatePaginationDisplay();
        this.$notify({
          text: this.$t('carbonCredit.activityLog.loadFailed'),
          type: 'error',
        });
      } finally {
        this.stopLoading();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cc-activity-log-table ::v-deep th {
  background-color: #117443 !important;
  color: white !important;
}

.cc-activity-log-table ::v-deep th .v-data-table__sort,
.cc-activity-log-table ::v-deep th .v-icon {
  color: white !important;
}

.cc-activity-log-table ::v-deep tbody tr:nth-child(even) {
  background-color: #f5f5f5;
}

.description-cell {
  white-space: normal;
  word-break: break-word;
  display: inline-block;
  max-width: 420px;
  text-align: left;
}

::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: white !important;
}
</style>
